<template>
  <div ref="xzxSeckbdRef" :class="ns.b()">
    <div v-if="slots?.header" :class="[ns.e('header')]">
      <slot name="header" />
    </div>
    <div v-if="ready" :class="[ns.e('content'), ns.is('kbd-' + currentType)]">
      <div v-for="(rows, index) in standardKbd" :key="index" :class="[nr]">
        <div
          v-for="item in rows"
          :key="item.key"
          :class="[ni, item?.class]"
          @click="onClickItem(item)"
        >
          <div v-if="!slots?.[item?.key]" :class="[nii]">
            <img
              v-if="item && !item?.isHandle"
              :src="item?.value"
              fit="cover"
            />
            <span v-else>{{ item?.value }}</span>
          </div>
          <slot v-else :name="item?.key" v-bind="{ type: currentType }" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, useSlots } from 'vue'
import { useSeckbd } from 'xzx-seckbd/vue'
import { cloneDeep, slice } from 'lodash-unified'
import { useNamespace } from '@xzx-design/hooks'
import { seckbdEmits, seckbdProps } from './seckbd'

defineOptions({
  name: 'XzxSeckbd',
})

const props = defineProps(seckbdProps)
const emits = defineEmits(seckbdEmits)

const ready = ref(false)
const xzxSeckbdRef: any = ref(null)
const inputEvent = ref()
const inputStartEnd = ref([0, 0])

const { uuid, lowerLetter, upperLetter, symbol, password, number, update } =
  useSeckbd({
    ...props.options,
    ready: () => {
      ready.value = true
    },
    error: (error: any) => {
      emits('error', error)
    },
  })

const map = {
  standard: 'lower',
  number: 'number',
}
const ct = (props.options?.type as 'standard' | 'number') || 'standard'

const currentType = ref(map[ct] || 'lower')

const slots = useSlots()

const ns = useNamespace('seckbd')

const ni = ns.e('item')
const nii = ns.e('item__inner')
const nr = ns.e('row')

const DELETE = {
  key: 'delete',
  value: '←',
  class: 'is-hdl hdl-delete',
  isHandle: true,
}

const UPPER = {
  key: 'upper',
  value: '↑',
  class: 'is-hdl hdl-upper',
  isHandle: true,
}

const LOWER = {
  key: 'lower',
  value: '↑',
  class: 'is-hdl hdl-lower',
  isHandle: true,
}

const NUMBER = {
  key: 'number',
  value: '123',
  class: 'is-hdl hdl-number',
  isHandle: true,
}

const LETTER =
  map[ct] !== 'number'
    ? {
        key: 'lower',
        value: 'ABC',
        class: 'is-hdl hdl-number',
        isHandle: true,
      }
    : {
        key: 'lb',
        value: '',
        class: 'is-hdl hdl-lb',
        isHandle: true,
      }

const SYMBOL = {
  key: 'symbol',
  value: '!?#',
  class: 'is-hdl hdl-symbol',
  isHandle: true,
}
const SPACE = {
  key: 'space',
  value: 'space',
  class: 'is-hdl hdl-space',
  isHandle: true,
}
const ENTER = {
  key: 'enter',
  value: '↵',
  class: 'is-hdl hdl-enter',
  isHandle: true,
}

const kbdMap: any = computed(() => ({
  lower: [...number.value, ...lowerLetter.value],
  upper: [...number.value, ...upperLetter.value],
  symbol: symbol.value,
  number: number.value,
}))

const standardKbd = computed(() => {
  let kbd = cloneDeep(kbdMap.value[currentType.value])

  if (['lower', 'upper'].includes(currentType.value)) {
    kbd.splice(29, 0, currentType.value === 'lower' ? UPPER : LOWER)
    kbd.splice(37, 0, DELETE)
    kbd = [...kbd, SYMBOL, NUMBER, SPACE, ENTER]
  }

  if (['symbol'].includes(currentType.value)) {
    kbd.splice(30, 0, DELETE)
    kbd = [...kbd, LETTER, NUMBER, SPACE, ENTER]
    return handleKbdElement(kbd, [10, 10, 10, 5])
  }

  if (['number'].includes(currentType.value)) {
    kbd.splice(9, 0, LETTER)
    kbd = [...kbd, DELETE]
    return handleKbdElement(kbd, [3, 3, 3, 3])
  }

  return handleKbdElement(kbd)
})

/**
 *
 * @example [1, 2, 3 ...]
 * @return size = [10, 10, 9, 9] -> [[10个], [10个], [9个], [9个], [剩余所有]]
 */
function handleKbdElement(array: any[], size = [10, 10, 9, 9]) {
  const groupSizes = size
  const result = []
  let startIndex = 0
  groupSizes.forEach((size) => {
    result.push(slice(array, startIndex, startIndex + size))
    startIndex += size
  })
  if (startIndex < array.length) {
    result.push(slice(array, startIndex))
  }
  return result
}

function onClickItem(item: any) {
  const ot = {
    uuid,
    password,
  }
  if (item?.isHandle) {
    const ch = ['upper', 'lower', 'number', 'symbol']
    if (ch?.includes(item.key) && map[ct] !== 'number')
      currentType.value = item.key
    if (item.key === 'delete') changeValue()
    emits('handle-click', item, ot)
  } else {
    emits('click', item.key, item, ot)
    if (inputEvent.value) {
      changeValue(item.key)
    }
  }
}

function changeValue(value?: string) {
  if (!inputEvent.value) return

  const inputElement = inputEvent.value
  const cursorStart = inputElement.selectionStart
  const cursorEnd = inputElement.selectionEnd

  const isMult = cursorStart === cursorEnd

  const newText = value || ''

  if (isMult && cursorEnd === 0 && !newText) return

  const isDelete = !newText && isMult

  const offset = isDelete ? (isMult ? 1 : 0) : 0

  const input =
    inputElement.value.slice(0, cursorStart - offset) +
    newText +
    inputElement.value.slice(cursorEnd)

  inputElement.value = input

  const newCursorPosition = newText
    ? cursorStart + newText.length
    : cursorStart - offset

  inputElement.selectionStart = inputElement.selectionEnd = newCursorPosition

  const inputEventToDispatch = new Event('input', {
    bubbles: true,
    cancelable: true,
  })
  inputElement.dispatchEvent(inputEventToDispatch)
}

function listenerMousedown(e: Event) {
  const target = e?.target as HTMLElement
  if (['INPUT', 'TEXTAREA', 'CONTENTEDITABLE']?.includes(target?.tagName)) {
    inputEvent.value = target
    inputStartEnd.value = [
      inputEvent.value.selectionStart,
      inputEvent.value.selectionEnd,
    ]
  }
}

function listenerSecClick(e: Event) {
  e.preventDefault()
}

onMounted(() => {
  document.addEventListener('mousedown', listenerMousedown)
  xzxSeckbdRef.value.addEventListener('mousedown', listenerSecClick)
})

defineExpose({
  uuid,
  update,
  password,
})
</script>
